import { Injectable, inject } from '@angular/core';
import { ContentApiServiceObservable } from '@kempoweroyj/ngx-content-api-client';
import { firstValueFrom } from 'rxjs';

/**
 * Fetches and caches the internal delivery token.
 * The token grants entries:read:internal scope, enabling internal entries in the CDN feed.
 *
 * Eligibility (e.g. sysAdmin) is decided by the caller; this leaf lib stays role-agnostic.
 * Exposes a `ready` promise that resolves once the token fetch is complete.
 * CDN services should await `ready` before reading `token` to avoid race conditions.
 */
@Injectable({ providedIn: 'root' })
export class InternalTokenService {
    readonly #contentApi = inject(ContentApiServiceObservable);

    #cachedToken: string | null = null;
    #pending: Promise<string | null> | null = null;

    /** Resolves when the token fetch is complete (or immediately for ineligible callers). */
    get ready(): Promise<string | null> {
        return this.#pending ?? Promise.resolve(null);
    }

    /**
     * Fetches the internal delivery token when the caller is eligible.
     * Ineligible callers pass false and receive null without a network call or cache write,
     * so a later eligible call still performs the real fetch.
     * Concurrent eligible callers share the same in-flight request.
     */
    fetchToken(isEligible: boolean): Promise<string | null> {
        if (!isEligible) return Promise.resolve(null);
        if (!this.#pending) {
            this.#pending = this.#doFetch();
        }
        return this.#pending;
    }

    async #doFetch(): Promise<string | null> {
        try {
            const res = await firstValueFrom(this.#contentApi.getInternalDeliveryToken());
            this.#cachedToken = res?.deliveryToken ?? null;
        } catch {
            // Non-critical — fall back to regular delivery token
        }

        return this.#cachedToken;
    }

    get token(): string | null {
        return this.#cachedToken;
    }
}
